import { useEffect, useState } from "react";
import { Play, ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { BookingModal } from "@/components/BookingModal";
import { LeadCaptureModal } from "@/components/LeadCapture";
import { StickyCTA } from "@/components/StickyCTA";
import { ScrollProgress } from "@/components/ScrollProgress";
import { BuyerForm } from "@/components/BuyerForm";
import { RisingMap } from "@/components/RisingMap";
import { Eyebrow, Reveal } from "@/components/bits";
import { useLang } from "@/i18n";

const IG_PROFILE = "https://www.instagram.com/vikramaditya.estates/";
const IG_REELS = "https://www.instagram.com/vikramaditya.estates/reels/";
const IG_POSTS = [
  "https://www.instagram.com/p/DaNhbDsTbRK/",
  "https://www.instagram.com/p/DZzZLQezCWs/",
  "https://www.instagram.com/p/DZZ3YYyTZjt/",
  "https://www.instagram.com/p/DZNMCs7zXP7/",
];

const CARDS = [
  { key: "vac", intent: "b.what.vac" },
  { key: "farm", intent: "b.what.farm" },
  { key: "coast", intent: "b.what.coast" },
  { key: "pent", intent: "b.what.pent" },
  { key: "else", intent: "b.what.else" },
];

const WHY = [
  { num: "I", tKey: "w1.t", cKey: "w1.c" },
  { num: "II", tKey: "w2.t", cKey: "w2.c" },
  { num: "III", tKey: "w3.t", cKey: "w3.c" },
];

export default function BuyersPage() {
  const { t } = useLang();
  const [bookingDoor, setBookingDoor] = useState(null);
  const [leadDoor, setLeadDoor] = useState(null);
  const [intent, setIntent] = useState("");
  const [buyerLocality, setBuyerLocality] = useState("");
  const [igFailed, setIgFailed] = useState(false);

  useEffect(() => {
    const setMeta = (sel, val) => {
      const el = document.head.querySelector(sel);
      if (el) el.setAttribute("content", val);
    };
    setMeta('meta[property="og:title"]', "Come home to something that lasts — Novare Estates");
    setMeta('meta[property="og:image"]', `${window.location.origin}/og-buyers.png`);
    setMeta('meta[name="twitter:image"]', `${window.location.origin}/og-buyers.png`);
  }, []);

  useEffect(() => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js";
    s.async = true;
    s.onload = () => window.instgrm?.Embeds.process();
    s.onerror = () => setIgFailed(true);
    document.body.appendChild(s);
    const timeout = setTimeout(() => {
      if (!window.instgrm) setIgFailed(true);
    }, 8000);
    return () => clearTimeout(timeout);
  }, []);

  const closeLead = () => {
    try {
      window.localStorage.setItem("novare_intro_seen", "1");
    } catch {}
    setLeadDoor(null);
  };

  const goToForm = (intentKey) => {
    if (intentKey) setIntent(intentKey);
    document.getElementById("buyer-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const pickLocality = (locality) => {
    if (locality) setBuyerLocality(locality);
    document.getElementById("buyer-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div id="top" className="bg-[#0E1613] text-[#EDE7DA] min-h-screen pb-14 md:pb-0">
      <ScrollProgress />
      <Nav onBookCall={() => setBookingDoor("Buyer")} onEnquire={() => setLeadDoor("general")} />

      <section
        data-testid="buyers-hero"
        className="min-h-screen flex flex-col justify-center max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20"
      >
        <p className="hero-reveal font-mono-label text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#B8894B]" style={{ animationDelay: "0.1s" }}>
          {t("by.eyebrow")}
        </p>
        <h1
          className="hero-reveal font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#EDE7DA] leading-[1.08] mt-6 max-w-4xl"
          style={{ animationDelay: "0.25s" }}
        >
          {t("by.h1a")}{" "}
          <em className="italic text-[#B8894B]">{t("by.h1em")}</em>
          {t("by.h1b")}
        </h1>
        <p
          className="hero-reveal font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed mt-6 max-w-2xl"
          style={{ animationDelay: "0.4s" }}
        >
          {t("by.sub")}
        </p>
        <div className="hero-reveal mt-10" style={{ animationDelay: "0.55s" }}>
          <button
            type="button"
            data-testid="buyers-hero-cta"
            onClick={() => goToForm("")}
            className="font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-8 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
          >
            {t("by.cta")}
          </button>
          <p className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#EDE7DA]/40 mt-5">
            {t("by.note")}
          </p>
        </div>
      </section>

      <section data-testid="buyers-cards" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>{t("by.cards.eyebrow")}</Eyebrow>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {CARDS.map((c, i) => (
              <Reveal key={c.key} delay={i * 90}>
                <button
                  type="button"
                  data-testid={`aspiration-card-${c.key}`}
                  onClick={() => goToForm(c.intent)}
                  className="group w-full h-full text-left border border-[#1A2A24] bg-[#131F1A] hover:border-[#B8894B]/70 transition-colors duration-300 p-8 md:p-10 flex flex-col justify-between min-h-[220px]"
                >
                  <h3 className="font-display text-xl md:text-2xl text-[#EDE7DA] tracking-tight leading-snug">
                    {t(`b.card.${c.key}.t`)}
                  </h3>
                  <div className="mt-6">
                    <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed">
                      {t(`b.card.${c.key}.c`)}
                    </p>
                    <ArrowUpRight className="w-4 h-4 text-[#B8894B] mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RisingMap onPickLocality={pickLocality} />

      <section data-testid="buyers-why" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>{t("why.eyebrow")}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.1] max-w-3xl">
            {t("why.h")}
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#1A2A24] border border-[#1A2A24] mt-14">
            {WHY.map((w) => (
              <div key={w.num} data-testid={`why-pillar-${w.num}`} className="bg-[#0E1613] p-8 md:p-10">
                <span className="font-display italic text-[#B8894B] text-xl">{w.num}.</span>
                <h3 className="font-display text-xl md:text-2xl text-[#EDE7DA] tracking-tight mt-4">
                  {t(w.tKey)}
                </h3>
                <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-4">
                  {t(w.cKey)}
                </p>
              </div>
            ))}
          </div>
          <p className="font-display italic text-xl md:text-2xl text-[#B8894B] tracking-tight mt-14 max-w-3xl leading-relaxed">
            “{t("why.quote")}”
          </p>
        </div>
      </section>

      <section data-testid="buyers-instagram" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>{t("ig.eyebrow")}</Eyebrow>
              <p className="font-light text-sm md:text-base text-[#EDE7DA]/60 leading-relaxed mt-4 max-w-xl">
                {t("ig.body")}
              </p>
            </div>
            <a
              data-testid="instagram-follow-btn"
              href={IG_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono-label text-[11px] tracking-[0.2em] uppercase border border-[#B8894B] text-[#B8894B] px-6 py-3 hover:bg-[#B8894B] hover:text-[#0E1613] transition-colors duration-300"
            >
              {t("ig.follow")}
            </a>
          </div>
          {igFailed ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
              {[1, 2, 3, 4].map((n) => (
                <a
                  key={n}
                  data-testid={`instagram-reel-${n}`}
                  href={IG_REELS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-[4/5] border border-[#1A2A24] bg-gradient-to-b from-[#1A2A24] to-[#0E1613] hover:border-[#B8894B]/70 transition-colors duration-300 flex items-center justify-center"
                >
                  <span className="w-12 h-12 border border-[#B8894B]/60 flex items-center justify-center group-hover:bg-[#B8894B] transition-colors duration-300">
                    <Play className="w-4 h-4 text-[#B8894B] group-hover:text-[#0E1613] transition-colors duration-300" />
                  </span>
                  <span className="absolute bottom-4 left-4 font-mono-label text-[9px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                    {t("ig.reel")} 0{n}
                  </span>
                  <span className="absolute bottom-4 right-4 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#B8894B] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t("ig.watch")}
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <div data-testid="instagram-embed-grid" className="grid sm:grid-cols-2 gap-4 mt-12">
              {IG_POSTS.map((url, i) => (
                <div
                  key={url}
                  data-testid={`instagram-embed-${i + 1}`}
                  className="border border-[#1A2A24] bg-[#131F1A] p-2 overflow-hidden"
                >
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={url}
                    data-instgrm-version="14"
                    style={{ background: "#0E1613", border: 0, margin: 0, padding: 0, width: "100%", minWidth: 0 }}
                  >
                    <a
                      href={`${url}?hl=en`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-6 font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#B8894B]"
                    >
                      {t("ig.watch")}
                    </a>
                  </blockquote>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section id="buyer-form" data-testid="buyers-form-section" className="py-24 md:py-32 border-t border-[#1A2A24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <Eyebrow>{t("bf.eyebrow")}</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.1]">
              {t("bf.h")}
            </h2>
            <p className="font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed mt-6">
              {t("bf.sub")}
            </p>
            <p className="font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#5E8570] mt-6">
              {t("bf.gulf")}
            </p>
          </div>
          <div className="md:col-span-7 border border-[#1A2A24] bg-[#131F1A] p-7 md:p-12">
            <BuyerForm intent={intent} locality={buyerLocality} />
          </div>
        </div>
      </section>

      <Footer onLead={() => setLeadDoor("general")} />
      <BookingModal door={bookingDoor} onClose={() => setBookingDoor(null)} />
      <LeadCaptureModal door={leadDoor} onClose={closeLead} />
      <StickyCTA onBook={() => setBookingDoor("Buyer")} onEnquire={() => setLeadDoor("general")} />
    </div>
  );
}
